/**
 * AnalyticsPanel — live read of the 4 cluster MVs (V2 §24) plus an
 * Investor Packet Export form (PDF / CSV / JSON).
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAnalyticsExport, useCluster, type ExportFormat, type ExportCluster } from "@/lib/founder/useFounderAdmin";
import SnapshotsPanel from "@/components/founder/SnapshotsPanel";
import { toast } from "@/hooks/use-toast";

const CLUSTER_LABEL: Record<ExportCluster, string> = {
  a: "A · Tech & AI",
  b: "B · Unit Economics",
  c: "C · Community Impact",
  d: "D · Diversity & Safety",
};

const ClusterTable = ({ cluster }: { cluster: ExportCluster }) => {
  const { fetchCluster } = useCluster();
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchCluster(cluster, 30).then((res) => {
      if (!active) return;
      setRows(res.rows);
      setLoading(false);
    });
    return () => { active = false; };
  }, [cluster, fetchCluster]);

  if (loading) return <p className="text-sm text-muted-foreground py-4">Loading…</p>;
  if (rows.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        No data yet. Events will appear here as members use the app.
      </p>
    );
  }
  const cols = Object.keys(rows[0]);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left border-b border-border">
            {cols.map((c) => <th key={c} className="py-2 pr-4 font-medium">{c}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0, 50).map((r, i) => (
            <tr key={i} className="border-b border-border/40">
              {cols.map((c) => (
                <td key={c} className="py-1.5 pr-4 font-mono">
                  {String(r[c] ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 50 && (
        <p className="text-xs text-muted-foreground mt-2">
          Showing first 50 of {rows.length} rows. Export for full data.
        </p>
      )}
    </div>
  );
};

export const AnalyticsPanel = () => {
  const { run, busy } = useAnalyticsExport();
  const today = new Date().toISOString().slice(0, 10);
  const ago90 = new Date(Date.now() - 90 * 86400_000).toISOString().slice(0, 10);
  const [from, setFrom] = useState(ago90);
  const [to, setTo] = useState(today);
  const [format, setFormat] = useState<ExportFormat>("pdf");

  const doExport = async () => {
    const res = await run({
      clusters: ["a","b","c","d"],
      from: new Date(from).toISOString(),
      to:   new Date(to).toISOString(),
      format,
      packet_type: "full_data_room",
    });
    if (res.ok) toast({ title: "Export ready", description: `${format.toUpperCase()} packet generated.` });
    else toast({ title: "Export failed", description: res.error ?? "", variant: "destructive" });
  };

  return (
    <div className="space-y-6">
      <Card className="p-5 space-y-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium">Investor Packet Export</h3>
            <Badge variant="outline">YMMH letterhead</Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Pulls all 4 clusters in your selected range. Every export is logged in admin_actions.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">From</label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-[160px]" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">To</label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-[160px]" />
          </div>
          <div className="flex gap-1">
            {(["pdf","csv","json","xlsx","zip"] as ExportFormat[]).map((f) => (
              <Button
                key={f}
                size="sm"
                variant={format === f ? "default" : "outline"}
                onClick={() => setFormat(f)}
              >
                {f.toUpperCase()}
              </Button>
            ))}
          </div>
          <Button onClick={doExport} disabled={busy}>
            {busy ? "Generating…" : "Export"}
          </Button>
        </div>
      </Card>

      <SnapshotsPanel />

      <Tabs defaultValue="a">
        <TabsList>
          {(Object.keys(CLUSTER_LABEL) as ExportCluster[]).map((c) => (
            <TabsTrigger key={c} value={c}>{CLUSTER_LABEL[c]}</TabsTrigger>
          ))}
        </TabsList>
        {(Object.keys(CLUSTER_LABEL) as ExportCluster[]).map((c) => (
          <TabsContent key={c} value={c}>
            <Card className="p-5">
              <ClusterTable cluster={c} />
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default AnalyticsPanel;
